import React from 'react';
import { motion } from 'framer-motion';
import { MailIcon, MessageCircleIcon, MonitorIcon, PresentationIcon, QrCodeIcon, Share2Icon, UsersIcon, MegaphoneIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const SNAK_SCREEN = 'https://cdn.magicpatterns.com/patterns/generated-images/f56b4488-53cb-4caa-ae80-a720cbf1c3eb.jpg';
interface Channel {
  label: string;
  icon: typeof PresentationIcon;
}
const LEFT_CHANNELS: readonly Channel[] = [{
  label: 'Email / CRM',
  icon: MailIcon
}, {
  label: 'Messaging apps',
  icon: MessageCircleIcon
}, {
  label: 'Website',
  icon: MonitorIcon
}, {
  label: 'Social',
  icon: Share2Icon
}];
const RIGHT_CHANNELS: readonly Channel[] = [{
  label: 'QR code',
  icon: QrCodeIcon
}, {
  label: 'Congress',
  icon: UsersIcon
}, {
  label: 'Rep iPad / IVA',
  icon: PresentationIcon
}, {
  label: 'Paid media',
  icon: MegaphoneIcon
}];
function ChannelTile({
  channel,
  align,
  delay
}: {
  channel: Channel;
  align: 'left' | 'right';
  delay: number;
}) {
  return <motion.li initial={{
    opacity: 0,
    x: align === 'left' ? -20 : 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay,
    duration: 0.45
  }} className={`flex min-h-0 flex-1 items-center gap-[clamp(0.8rem,1.5vw,1.4rem)] rounded-2xl border border-[var(--fg)]/10 bg-white px-[clamp(1rem,1.8vw,1.75rem)] shadow-sm ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-60" data-mp="3tr8n8o19u0ka">
      <span className="flex h-[clamp(2.8rem,4vw,4.25rem)] w-[clamp(2.8rem,4vw,4.25rem)] flex-shrink-0 items-center justify-center rounded-2xl bg-[var(--brand)]/10" data-id="element-61" data-mp="h9k6l80qwhr1">
        <channel.icon className="h-[clamp(1.35rem,2vw,2.1rem)] w-[clamp(1.35rem,2vw,2.1rem)] text-[var(--brand)]" aria-hidden="true" data-id="element-62" data-mp="qb1iee74dx9x" />
      </span>
      <span className={`min-w-0 flex-1 text-[clamp(1.15rem,1.9vw,1.8rem)] font-extrabold leading-tight tracking-[-0.03em] text-[var(--fg)] ${align === 'right' ? 'text-right' : ''}`} data-id="element-63" data-mp="2n54mysk5u93d">
        {channel.label}
      </span>
    </motion.li>;
}
export function SlideChannelEntryPoints({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner data-id="element-65" data-mp="89w3f2ef82kd">Meet HCPs wherever attention already exists.</SlideBanner>} data-id="element-64" data-mp="89453jtfvqqe">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex-shrink-0" data-id="element-66" data-mp="37yd5o7ls7hx">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-67" data-mp="d6w7x8vmgx9d">
          Distribution
        </p>
        <h2 className="max-w-4xl text-[clamp(1.8rem,3.4vw,3.1rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-68" data-mp="mxh1uj0cpdly">
          One experience.{' '}
          <span className="text-[var(--brand)]" data-id="element-69" data-mp="10xjik1wxa0zm">Many entry points.</span>
        </h2>
      </motion.header>

      <div className="mt-[clamp(0.9rem,1.8vw,1.6rem)] flex min-h-0 flex-1 items-stretch gap-[clamp(0.75rem,1.6vw,1.75rem)]" data-id="element-70" data-mp="vw2i27icogjk">
        <ul className="flex min-h-0 flex-1 flex-col gap-[clamp(0.45rem,0.9vw,0.85rem)]" data-id="element-71" data-mp="1kj1rnl3cbl0w">
          {LEFT_CHANNELS.map((channel, index) => <ChannelTile key={channel.label} channel={channel} align="left" delay={0.25 + index * 0.08} data-id="element-72" data-mp="15kzohc0811k6" data-mpi={index} />)}
        </ul>

        <motion.div initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.15,
        duration: 0.55
      }} className="relative flex min-h-0 w-[20%] flex-shrink-0 flex-col items-center justify-center" data-id="element-73" data-mp="wspd3rdlaq1c">
          <img src={SNAK_SCREEN} alt="A single Snak experience shared across every channel" className="min-h-0 max-h-full w-auto rounded-[1.25rem] object-contain drop-shadow-[0_22px_50px_rgba(23,32,51,0.2)]" data-id="element-74" data-mp="rtl719lyp8f8" />
          <span className="mt-[clamp(0.4rem,0.8vw,0.7rem)] flex-shrink-0 rounded-full bg-[var(--brand)] px-[clamp(0.6rem,1.1vw,1rem)] py-[clamp(0.2rem,0.4vw,0.35rem)] text-[clamp(0.55rem,0.8vw,0.74rem)] font-bold uppercase tracking-[0.18em] text-white" data-id="element-75" data-mp="hy1bwpf82drj">
            One Snak
          </span>
        </motion.div>

        <ul className="flex min-h-0 flex-1 flex-col gap-[clamp(0.45rem,0.9vw,0.85rem)]" data-id="element-76" data-mp="3s9i0rp91n55v">
          {RIGHT_CHANNELS.map((channel, index) => <ChannelTile key={channel.label} channel={channel} align="right" delay={0.25 + index * 0.08} data-id="element-77" data-mp="3o8oe0z7c7wek" data-mpi={index} />)}
        </ul>
      </div>
    </InteriorSlide>;
}